import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';

const MOCKUP_IMAGE_URL = '/mockups/mockup-full.jpg';

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export default function MockupPreviewPage() {
  const q = useQuery();
  const business = q.get('business') || 'Your Business';
  const site = q.get('site') || '';
  const mockup = q.get('mockup') || '';
  const mockupFull = q.get('mockupFull') || '';
  const current = q.get('current') || '';
  const leadId = q.get('leadId') || '';

  const beforeImage = useMemo(() => {
    if (current) return current;
    if (leadId) return `https://neurasites.ai/mockups/generated/${leadId}-before.png`;
    if (!site) return '';
    const target = site.startsWith('http') ? site : `https://${site}`;
    return `https://image.thum.io/get/width/1200/crop/900/noanimate/${target}`;
  }, [current, leadId, site]);

  const generatedHeroPng = leadId ? `https://neurasites.ai/mockups/generated/${leadId}.png` : '';
  const generatedHeroJpg = leadId ? `https://neurasites.ai/mockups/generated/${leadId}.jpg` : '';
  const heroMockupSrc = mockup || generatedHeroPng || generatedHeroJpg || MOCKUP_IMAGE_URL;
  const fullMockupCandidates = useMemo(() => {
    const candidates = [
      mockupFull,
      leadId ? `https://neurasites.ai/mockups/generated/${leadId}-full.png` : '',
      leadId ? `https://neurasites.ai/mockups/generated/${leadId}-full.jpg` : '',
    ].filter(Boolean);

    return candidates.length ? candidates : [heroMockupSrc];
  }, [heroMockupSrc, leadId, mockupFull]);
  const [fullMockupIndex, setFullMockupIndex] = useState(0);
  const afterMockupSrc = fullMockupCandidates[Math.min(fullMockupIndex, fullMockupCandidates.length - 1)] || heroMockupSrc;
  const fullMockupKey = fullMockupCandidates.join('|');

  const [syncScroll, setSyncScroll] = useState(false);
  const beforeRef = useRef<HTMLDivElement | null>(null);
  const afterRef = useRef<HTMLDivElement | null>(null);
  const activePaneRef = useRef<'before' | 'after' | null>(null);

  useEffect(() => {
    setFullMockupIndex(0);
  }, [fullMockupKey]);

  useEffect(() => {
    const beforeEl = beforeRef.current;
    const afterEl = afterRef.current;
    if (!beforeEl || !afterEl) return;

    let suppressBefore = false;
    let suppressAfter = false;

    const sync = (source: HTMLDivElement, target: HTMLDivElement, sourceName: 'before' | 'after') => {
      if (!syncScroll) return;
      if (activePaneRef.current && activePaneRef.current !== sourceName) return;

      if (sourceName === 'before' && suppressBefore) {
        suppressBefore = false;
        return;
      }
      if (sourceName === 'after' && suppressAfter) {
        suppressAfter = false;
        return;
      }

      const maxSrc = Math.max(1, source.scrollHeight - source.clientHeight);
      const maxTgt = Math.max(1, target.scrollHeight - target.clientHeight);
      const ratio = source.scrollTop / maxSrc;
      const nextTop = ratio * maxTgt;

      if (Math.abs(target.scrollTop - nextTop) < 1) return;

      if (sourceName === 'before') suppressAfter = true;
      else suppressBefore = true;

      target.scrollTop = nextTop;
    };

    const onBefore = () => sync(beforeEl, afterEl, 'before');
    const onAfter = () => sync(afterEl, beforeEl, 'after');

    beforeEl.addEventListener('scroll', onBefore, { passive: true });
    afterEl.addEventListener('scroll', onAfter, { passive: true });

    return () => {
      beforeEl.removeEventListener('scroll', onBefore);
      afterEl.removeEventListener('scroll', onAfter);
    };
  }, [syncScroll]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEOHead
        title={`Concept Preview for ${business} | NeuraSites AI`}
        description="Private concept preview for a NeuraSites AI website direction."
        canonicalUrl="https://neurasites.ai/mockup-preview"
        robots="noindex, nofollow"
      />
      <Header />
      <section className="max-w-7xl mx-auto px-4 py-12">
        <p className="text-sm text-blue-600 font-semibold">NeuraSites AI Concept Preview</p>
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mt-2">Modern Website Direction for {business}</h1>
        <p className="mt-3 text-gray-600 dark:text-gray-300">This is a fast concept pass to show the style and structure we can ship quickly.</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs font-medium">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700 dark:bg-gray-800 dark:text-gray-200">1. Current full homepage</span>
          <span className="rounded-full bg-blue-100 px-3 py-1 text-blue-700 dark:bg-blue-950/50 dark:text-blue-200">2. Outreach hero preview</span>
          <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200">3. Redesigned full homepage</span>
        </div>

        <div className="mt-8 rounded-2xl brand-soft-card border-sky-200/70 p-5 dark:border-slate-700">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-blue-700 dark:text-blue-300">Step 2 of 3</p>
              <h2 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">Outreach hero preview</h2>
              <p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
                This tighter crop is the click-driving email image. The full comparison lives below so the lead can review the complete homepage redesign.
              </p>
            </div>
            <span className="inline-flex w-fit rounded-full border border-blue-300/60 bg-white/80 px-3 py-1 text-xs font-semibold text-blue-700 dark:border-blue-500/40 dark:bg-gray-900/70 dark:text-blue-200">
              Email-ready preview
            </span>
          </div>

          <div className="mt-5 overflow-hidden rounded-xl border border-blue-200/70 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <img src={heroMockupSrc} alt="NeuraSites AI outreach hero preview" className="w-full h-auto block" />
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setSyncScroll((v) => !v)}
            className={`px-3 py-2 rounded-md text-sm border transition ${syncScroll ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}
          >
            {syncScroll ? 'Linked scroll: ON' : 'Linked scroll: OFF'}
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">Toggle synchronized full-page comparison scrolling</span>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* Before: Current site snapshot */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800">
            <p className="text-xs uppercase tracking-wide text-white mb-2 inline-block px-2 py-1 rounded bg-gray-900/80">Step 1 of 3 · Current full homepage</p>
            {beforeImage ? (
              <div ref={beforeRef} onMouseEnter={() => { activePaneRef.current = 'before'; }} onMouseLeave={() => { activePaneRef.current = null; }} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 h-[560px] overflow-y-auto">
                <img src={beforeImage} alt="Current website snapshot" className="w-full h-auto block" />
              </div>
            ) : (
              <div className="h-64 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500">No source URL provided</div>
            )}
            <p className="mt-2 text-xs text-gray-500 break-all">Source URL: {site || 'none'}</p>
          </div>

          {/* After: NeuraSites AI generated mockup */}
          <div className="rounded-xl border border-emerald-300/40 p-4 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/20">
            <p className="text-xs uppercase tracking-wide text-white mb-2 inline-block px-2 py-1 rounded bg-emerald-700/90">Step 3 of 3 · Redesigned full homepage</p>
            <div className="rounded-lg bg-white dark:bg-gray-900 p-5 border border-gray-200 dark:border-gray-700">
              <div ref={afterRef} onMouseEnter={() => { activePaneRef.current = 'after'; }} onMouseLeave={() => { activePaneRef.current = null; }} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 h-[560px] overflow-y-auto">
                <img
                  src={afterMockupSrc}
                  alt="NeuraSites AI full homepage mockup"
                  className="w-full h-auto block"
                  onError={() => {
                    setFullMockupIndex((value) => (
                      value < fullMockupCandidates.length - 1 ? value + 1 : value
                    ));
                  }}
                />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-4">{business}</h2>
              <p className="text-gray-600 dark:text-gray-300 mt-2">A cleaner, conversion-focused homepage with stronger authority messaging and clearer booking CTA.</p>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-blue-200/60 p-5 bg-blue-50/70 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300">What this refresh improves</h3>
          <ul className="mt-3 grid md:grid-cols-2 gap-2 text-sm text-gray-700 dark:text-gray-300">
            <li>• New headline + trust proof above the fold</li>
            <li>• Service blocks with clearer value proposition</li>
            <li>• Faster mobile UX and SEO-friendly structure</li>
            <li>• Integrated lead capture + booking flow</li>
          </ul>
          <div className="mt-6 flex gap-3">
            <Link to="/pricing" className="px-4 py-2 rounded bg-blue-600 text-white">View Packages</Link>
            <Link to="/vibe-check" className="px-4 py-2 rounded border border-blue-500 text-blue-700 dark:text-blue-300">Start Vibe Check</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
